import { Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { News } from 'src/shared/models/notifications/news.model';
import { Reply } from 'src/shared/models/notifications/reply.model';
import { Booking } from 'src/shared/models/booking.model';
import { NewsProvider } from './news.provider';
import { HttpUtils } from 'src/shared/utils/http-utils';
import { BookingProvider } from './booking.provider';
import { RequestProvider } from './request.provider';
import { LocalStorageService } from './local-storage.service';
import { AuthenticatedHttpService } from './authenticatedHttpService.service';
import * as fp from 'lodash/fp';

export enum SyncWrapResult {
  Error,
  SyncLater,
  Success,
  SuccessAndSync,
}

@Injectable({ providedIn: 'root' })
export class SyncService {
  static readonly RETRY_PENDING_REPLIES = 'RETRY_PENDING_REPLIES';
  static readonly RETRY_PENDING_REQUESTS = 'RETRY_PENDING_REQUESTS';
  static readonly RETRY_SET_READ_STATUS = 'RETRY_SET_READ_STATUS';
  static readonly STORAGE_OFFLINE_BOOKINGS = 'STORAGE_OFFLINE_BOOKINGS';

  static GetKey(key: string, userId: string) {
    return `${key}_${userId}`;
  }

  private readonly _bookingsSynced = signal<boolean>(false);
  public readonly bookingsSynced = this._bookingsSynced.asReadonly();
  public readonly bookingsSynced$ = toObservable(this._bookingsSynced);

  private _pendingNews = signal<Array<News>>([]);
  public readonly pendingNews$ = toObservable(this._pendingNews);

  /** Per-logical-key promise chain used to serialize read-modify-write storage access. */
  private readonly storageLocks = new Map<string, Promise<unknown>>();

  // Simplifying wrap calls.
  // Either All or None messageKeys are required.
  syncMap = {
    [SyncService.RETRY_PENDING_REPLIES]: {
      service: this.newsService,
      request: this.newsService.reply,
      syncMethod: this.syncReplies,
      successMessageKey: 'Reply_sent',
      successAndSyncMessageKey: 'Replies_sent_and_synced',
      syncLaterHeaderMessageKey: 'No_Connection_try_later_Header',
      syncLaterMessageKey: 'No_Connection_try_later',
      equals: Reply.equals,
    },
    [SyncService.RETRY_PENDING_REQUESTS]: {
      service: this.requestsService,
      request: this.requestsService.createApplication,
      syncMethod: this.syncRequests,
      successMessageKey: 'NewsRequest_sent',
      successAndSyncMessageKey: 'NewsRequest_sent_and_synced',
      syncLaterHeaderMessageKey: 'No_Connection_try_later_Header',
      syncLaterMessageKey: 'No_Connection_try_later',
    },
    [SyncService.RETRY_SET_READ_STATUS]: {
      service: this.newsService,
      request: this.newsService.read,
      syncMethod: this.syncReadStatus,
      syncLaterHeaderMessageKey: 'No_Connection_try_later_Header',
      syncLaterMessageKey: 'No_Connection_try_later',
    },
    [SyncService.STORAGE_OFFLINE_BOOKINGS]: {
      service: this.bookingProvider,
      request: this.bookingProvider.bookData,
      syncMethod: this.syncBookings,
      successMessageKey: 'Booking_sent',
      successAndSyncMessageKey: 'Booking_sent_and_synced',
      syncLaterHeaderMessageKey: 'No_Connection_try_later_Header',
      syncLaterMessageKey: 'No_Connection_try_later',
      equals: Booking.equals,
    },
  };

  constructor(
    private storage: LocalStorageService,
    private translateService: TranslateService,
    private newsService: NewsProvider,
    private bookingProvider: BookingProvider,
    private requestsService: RequestProvider
  ) {
    // Initialize pending news after observable is created
    this.initPendingNews();
  }

  async wrapRequest<T, R>(
    mapKey: string,
    data: T
  ): Promise<{
    status: SyncWrapResult;
    result: R | undefined;
    error: any;
    message: string;
    headerMessage: string;
  }> {
    const meta = this.syncMap[mapKey as keyof typeof this.syncMap];
    if (!meta) throw new Error('Invalid key');

    let [status, result, error] = [SyncWrapResult.Error, , ,];

    try {
      const boundRequest = meta.request.bind(meta.service);

      result = (await HttpUtils.handle<R>({ request: boundRequest(data as any) })) as any;

      const edgeCase = await this.unpend(data, mapKey, fp.get('equals', meta));
      if (edgeCase) {
        // Handling Edge case with queue emptied by unpend. Showing proper notif
        status = SyncWrapResult.SuccessAndSync;
      } else {
        const anySynced = await meta.syncMethod.apply(this);
        status = anySynced ? SyncWrapResult.SuccessAndSync : SyncWrapResult.Success;
      }
    } catch (err) {
      error = err as any;
      if (this.isPermanentRejection(error)) {
        // Server definitively rejected the request (e.g. 400/404/409/422).
        // Retrying would never succeed, so surface the error instead of queueing it.
        status = SyncWrapResult.Error;
        console.error(`wrapRequest: permanent rejection for "${mapKey}", not queued.`, error);
      } else {
        // Transient or unknown failure (timeout, 5xx, network, auth) — queue the
        // action for a later retry rather than dropping the user's input.
        await this.pend(data, mapKey, fp.get('equals', meta));
        status = SyncWrapResult.SyncLater;
      }
    }

    this.sideEffects(mapKey);

    const message = await this.describe(status, meta, error);
    return {
      status,
      result,
      error,
      ...message,
    };
  }
  private async describe(
    status: SyncWrapResult,
    meta: any,
    error: any
  ): Promise<{ message: string; headerMessage: string }> {
    let message: string | undefined = '';
    let headerMessage: string | undefined = '';

    if (meta.successMessageKey) {
      const messageOrObs = this.translateService.get(meta.successMessageKey);
      message = await this.resolveToString(messageOrObs);
    }

    if (meta.successAndSyncMessageKey && status === SyncWrapResult.SuccessAndSync) {
      const messageOrObs = this.translateService.get(meta.successAndSyncMessageKey);
      message = await this.resolveToString(messageOrObs);
    }

    if (meta.syncLaterMessageKey && status === SyncWrapResult.SyncLater) {
      const messageOrObs = this.translateService.get(meta.syncLaterMessageKey);
      message = await this.resolveToString(messageOrObs);

      if (meta.syncLaterHeaderMessageKey) {
        const headerOrObs = this.translateService.get(meta.syncLaterHeaderMessageKey);
        headerMessage = await this.resolveToString(headerOrObs);
      }
    }

    if (status === SyncWrapResult.Error) {
      message = HttpUtils.stringifyError(error);
    }

    return { message: message ?? '', headerMessage: headerMessage ?? '' };
  }
  private async resolveToString(
    value: string | Observable<string> | undefined
  ): Promise<string | undefined> {
    if (value instanceof Observable) {
      return await value.toPromise();
    }
    return value ?? '';
  }

  private async sideEffects(mapKey: string) {
    try {
      if (mapKey === SyncService.RETRY_PENDING_REPLIES) this.initPendingNews();
    } catch (error) {
      console.error(error);
    }
  }

  async pend<T>(target: T | T[], storeKey: string, customEquals?: (a: T, b: T) => boolean) {
    if (!target || !storeKey) throw new Error('Invalid input');

    const incoming: T[] = Array.isArray(target) ? target : [target];

    // Serialize the read-modify-write so a concurrent syncItems()/unpend() on the same
    // key cannot overwrite this item with a stale snapshot (lost-update race).
    await this.withStorageLock(storeKey, async () => {
      const alreadyPending: T[] = (await this.getFromStorage(storeKey)) || [];

      // Ensure the customEquals function has the correct type
      const items = alreadyPending.length
        ? (fp.uniqWith(customEquals || fp.isEqual, [...alreadyPending, ...incoming]) as T[])
        : incoming;

      const keyWithUserId = await this.getStoreKey(storeKey);
      await this.storage.set(keyWithUserId, items);
    });
  }
  async unpend<T>(target: T | Array<T>, storeKey: string, customEquals?: Function) {
    if (!target || !storeKey) throw new Error('Invalid input');

    const items = target instanceof Array ? target : [target];

    // Serialize the read-modify-write (see pend) to avoid lost updates.
    return this.withStorageLock(storeKey, async () => {
      let alreadyPending = (await this.getFromStorage(storeKey)) || [];
      const queNotEmpty = fp.get('length', alreadyPending);

      if (alreadyPending && alreadyPending.length) {
        alreadyPending = alreadyPending.filter(
          (ap: any) => !items.some((item) => (customEquals || fp.isEqual)(ap, item))
        );
      }
      const queEmpty = !fp.get('length', alreadyPending);

      const keyWithUserId = await this.getStoreKey(storeKey);
      await this.storage.set(keyWithUserId, alreadyPending);

      return queNotEmpty && queEmpty;
    });
  }

  [syncAllKey: string]: any;
  async syncAll(): Promise<boolean> {
    const pendingKey = `${this.syncAll.name}Pending`;
    if (this[pendingKey]) {
      return false;
    }
    this[pendingKey] = true;

    let somethingSynced = false;

    const syncs: Array<Promise<boolean>> = [];

    try {
      // those sync method must not throw
      syncs.push(this.syncReplies());
      syncs.push(this.syncRequests());
      syncs.push(this.syncReadStatus());
      syncs.push(this.syncBookings());
      somethingSynced = (await Promise.all(syncs)).reduce((a, v) => a || v);
    } catch (error) {
      console.error(error);
    }

    delete this[`${this.syncAll.name}Pending`];
    return somethingSynced;
  }
  async syncReplies() {
    const meta = this.syncMap[SyncService.RETRY_PENDING_REPLIES];
    const anySynced = await this.syncItems<Reply>(
      meta.service,
      meta.request,
      SyncService.RETRY_PENDING_REPLIES
    );
    if (anySynced) this.initPendingNews();
    return anySynced;
  }
  async syncRequests() {
    const meta = this.syncMap[SyncService.RETRY_PENDING_REQUESTS];
    return await this.syncItems<Reply>(
      meta.service,
      meta.request,
      SyncService.RETRY_PENDING_REQUESTS
    );
  }
  async syncReadStatus() {
    const meta = this.syncMap[SyncService.RETRY_SET_READ_STATUS];
    return await this.syncItems(meta.service, meta.request, SyncService.RETRY_SET_READ_STATUS);
  }
  async syncBookings() {
    const meta = this.syncMap[SyncService.STORAGE_OFFLINE_BOOKINGS];
    const isSynced = await this.syncItems<Booking>(
      meta.service,
      meta.request,
      SyncService.STORAGE_OFFLINE_BOOKINGS
    );
    this._bookingsSynced.set(isSynced);
    return isSynced;
  }
  // Returns:
  // false if nothing synced
  // undefined on error
  // true in something has been synced
  private async syncItems<T>(
    context: any,
    sync: Function,
    storeKey: string,
    extraParams = []
  ): Promise<boolean> {
    const pendingKey = `${this.syncItems.name}${storeKey}Pending`;
    if (this[pendingKey]) {
      return false;
    }
    this[pendingKey] = true;

    try {
      const items = await this.getFromStorage(storeKey);
      if (!items || !items.length) {
        return false;
      }

      if (
        storeKey === SyncService.STORAGE_OFFLINE_BOOKINGS ||
        storeKey === 'STORAGE_NOT_SYNC_BOOKINGS'
      ) {
        items.forEach((item: any) => (item.isOfflineBooking = true));
      }

      // Items to drop from the queue afterwards: either synced OK, or permanently
      // rejected by the server (retrying would never succeed). Everything else
      // (timeout, 5xx, auth, network, unknown) is KEPT for the next attempt — it is
      // never silently dropped.
      const toRemove: any[] = [];
      let failedCount = 0;

      await Promise.all(
        items.map(async (item: any) => {
          try {
            await sync.apply(context, [item, ...extraParams]);
            toRemove.push(item);
          } catch (error) {
            ++failedCount;
            if (this.isPermanentRejection(error)) {
              console.error(
                `syncItems: permanent rejection for "${storeKey}", dropping item.`,
                item,
                error
              );
              toRemove.push(item);
            } else {
              console.error(
                `syncItems: transient failure for "${storeKey}", will retry next sync.`,
                error
              );
            }
          }
        })
      );

      // Persist under the per-key lock and merge with anything pended during the
      // network window: remove only the items we processed, keep the rest (including
      // items queued by a concurrent pend()).
      const equals = (fp.get([storeKey, 'equals'], this.syncMap) as Function) || fp.isEqual;
      await this.withStorageLock(storeKey, async () => {
        const current = (await this.getFromStorage(storeKey)) || [];
        const remaining = current.filter((c: any) => !toRemove.some((r: any) => equals(c, r)));
        const keyWithUserId = await this.getStoreKey(storeKey);
        await this.storage.set(keyWithUserId, remaining);
      });

      return items.length > failedCount;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      delete this[pendingKey];
    }
  }

  private async getStoreKey(storeKey: string) {
    const userId = await this.getUserId();
    return `${storeKey}_${userId}`;
  }

  /**
   * Runs `op` exclusively with respect to other storage operations on the same
   * logical key, preventing lost-update races between pend/unpend/syncItems.
   * The map is bounded (one entry per logical store key).
   */
  private withStorageLock<T>(storeKey: string, op: () => Promise<T>): Promise<T> {
    const prev = this.storageLocks.get(storeKey) ?? Promise.resolve();
    const run = prev.then(() => op());
    // Chain the next op on a tail that never rejects, so one failure can't block the queue.
    this.storageLocks.set(
      storeKey,
      run.then(
        () => undefined,
        () => undefined
      )
    );
    return run;
  }

  /**
   * True for a definitive client-side rejection (a 4xx other than auth) that retrying
   * will never resolve, so the item must not be re-queued. Auth errors (401/403),
   * timeouts, 5xx and network errors are treated as transient and remain retryable.
   */
  private isPermanentRejection(error: any): boolean {
    const status = error?.status;
    return (
      typeof status === 'number' &&
      status >= 400 &&
      status < 500 &&
      status !== 401 &&
      status !== 403
    );
  }

  private async getFromStorage(key: string) {
    const keyWithUserId = await this.getStoreKey(key);
    const storageDataWithUserId = await this.storage.get(keyWithUserId);
    return storageDataWithUserId;
  }

  private async getUserId(): Promise<string> {
    const authData = await this.storage.get(AuthenticatedHttpService.AUTHENTICATION_DATA);
    if (!authData || !authData.jwt) return '';
    return authData.jwt.sub;
  }

  private async initPendingNews() {
    // pendingNews surfaces unsynced replies in the news/messages list. Requests
    // (RETRY_PENDING_REQUESTS) are a different page and item shape, so they are
    // intentionally not included here.
    const replies = await this.getFromStorage(SyncService.RETRY_PENDING_REPLIES);
    this._pendingNews.set([...(replies || [])]);
  }
}
